import { showNotification } from "@mantine/notifications"
import { useMutation, useQuery } from "@tanstack/react-query"

import {
  EventsRequests,
  EventsResponses,
} from "@costory/types/endpoints/events"
import { GitHubResponses } from "@costory/types/endpoints/github"
import { Filters } from "@costory/types/filters"

import dayjs, { Dayjs } from "@costory/shared/dayjs"

import { apiClient } from "@costory/front/lib/apiClient"
import { queryClient } from "@costory/front/lib/queryClient"

export function useCreateNewEventMutation({
  closeModal,
  selectedDateRange,
  currentView,
}: {
  closeModal: () => void
  selectedDateRange?: { from: Dayjs; to: Dayjs }
  currentView: Filters
}) {
  return useMutation({
    mutationFn: async (form: EventsRequests.EventForm) => {
      const response = await apiClient.post<EventsResponses.New>(
        "/events/new",
        form,
      )
      return response.data
    },
    onSuccess: async (_data, variables) => {
      showNotification({
        color: "green",
        title: "Events Created",
        message: "",
      })

      await queryClient.invalidateQueries({ queryKey: ["events"] })
      checkIfEventIsOutsideRange({
        variables,
        selectedDateRange,
        currentView,
        isNewEvent: true,
      })
      closeModal()
    },
    onError: (error) => {
      showNotification({
        color: "red",
        title: "Error",
        message: error.message,
      })
    },
  })
}
export function useEditEventMutation({
  closeModal,
  currentView,
  selectedDateRange,
}: {
  closeModal: () => void
  currentView: Filters
  selectedDateRange?: { from: Dayjs; to: Dayjs }
}) {
  return useMutation({
    mutationFn: async (form: EventsRequests.EventForm) => {
      const response = await apiClient.put<EventsResponses.New>(
        `/events/${form.id}`,
        form,
      )
      return response.data
    },
    onSuccess: async (_, variables) => {
      showNotification({
        color: "green",
        title: "Events Updated",
        message: "",
      })
      await queryClient.invalidateQueries({ queryKey: ["events"] })
      checkIfEventIsOutsideRange({
        variables,
        selectedDateRange,
        currentView,
        isNewEvent: false,
      })
      closeModal()
    },
    onError: (error) => {
      showNotification({
        color: "red",
        title: "Error",
        message: error.message,
      })
    },
  })
}

export const useTagsQuery = () => {
  return useQuery({
    queryKey: ["events", "tags"],
    queryFn: async () => {
      const response =
        await apiClient.get<EventsResponses.Labels>("/events/tags")
      return response.data
    },
  })
}

export function useDeleteEventMutation() {
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.delete<EventsResponses.New>(
        `/events/${id}`,
      )
      return response.data
    },
    onSuccess: async () => {
      showNotification({
        color: "green",
        title: "Event Deleted",
        message: "",
      })
      await queryClient.invalidateQueries({ queryKey: ["events"] })
    },
    onError: (error: { error: string }) => {
      showNotification({
        color: "red",
        title: "Error",
        message: error.error,
      })
    },
  })
}

export const useGithubReposQuery = () => {
  return useQuery({
    queryKey: ["events", "github", "repos"],
    queryFn: async () => {
      const response =
        await apiClient.get<GitHubResponses.Repos>("/github/repos")
      return response.data
    },
  })
}

const checkIfEventIsOutsideRange = ({
  variables,
  selectedDateRange,
  currentView,
  isNewEvent,
}: {
  variables: EventsRequests.EventForm
  selectedDateRange?: { from: Dayjs; to: Dayjs }
  currentView?: Filters
  isNewEvent?: boolean
}) => {
  // Check if event is outside selected date range
  const eventDayjs = dayjs(variables.date)
  const isOutsideRange =
    selectedDateRange || currentView
      ? eventDayjs.isBefore(
          selectedDateRange?.from ?? currentView?.from,
          "day",
        ) || eventDayjs.isAfter(selectedDateRange?.to ?? currentView?.to, "day")
      : false
  if (isOutsideRange) {
    showNotification({
      color: "yellow",
      title: isNewEvent
        ? "Created event is outside selected date range"
        : "Updated event is outside selected date range",
      message: isNewEvent
        ? "The created event is outside selected date range - update the date filter to see the event"
        : "The updated event is outside selected date range - update the date filter to see the event",
    })
  }
}
